import { useCallback, useLayoutEffect } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import {
  type CustomRoleTaxonomyId,
  getAdaptiveRoleTaxonomyValue,
  type SelectionListIdNames,
  type TreeItem,
} from '@revelio/iso-utility';

import { useCurrentRoleTaxonomyVersion } from '../../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useSelectionLists } from '../../../../../shared/selection-lists/hooks/useSelectionLists';
import type { SelectionList as RicsSelectionList } from '../../../../../shared/selection-lists/types';
import type { EntitySelections } from '../../report-builder.model';
import {
  selectAllReports,
  useReportBuilderSelector,
} from '../../report-builder.repository';
import { updateReportEntitySelections } from './report-entity-selection.utils';
import { HIDDEN_REPORT_FIELDS } from './report-hidden-columns';
import { adaptiveRicsFromReportSelections } from './reports-role-custom-taxonomy-helpers';
import type { ReportEntityConfig } from './utils';

export const CUSTOM_TAXONOMY_DEFAULT_ID = 'default';

export const getRoleTaxonomyFilterValue = (
  taxonomyId: string
): CustomRoleTaxonomySelection => {
  if (taxonomyId === 'default') {
    return { default: true };
  }

  if (taxonomyId.includes('rics_k10_')) {
    const id = taxonomyId.split('rics_k10_')[1];
    return { ricsK10Id: id };
  }

  if (taxonomyId.includes('rics_k50_')) {
    const id = taxonomyId.split('rics_k50_')[1];
    return { ricsK50Id: id };
  }

  return { default: true };
};

const selectionFromCustomRoleTaxonomyId = (
  taxonomyId: CustomRoleTaxonomyId
): CustomRoleTaxonomySelection => {
  const id =
    taxonomyId === 'custom_role_default'
      ? CUSTOM_TAXONOMY_DEFAULT_ID
      : (taxonomyId.split('custom_role_')[1] ?? CUSTOM_TAXONOMY_DEFAULT_ID);
  return getRoleTaxonomyFilterValue(id);
};

/**
 * Checks if a selection key represents a hidden custom taxonomy selection
 */
export const isHiddenCustomTaxonomySelectionKey = (selectionKey: string) => {
  const key = selectionKey.toLowerCase();

  return (
    key === HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY ||
    key === HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY_VERSION
  );
};

const createCustomTaxonomyTreeItemFromId = (
  taxonomyId: CustomRoleTaxonomyId
): TreeItem<string> => {
  const customTaxonomyId =
    taxonomyId === 'custom_role_default'
      ? CUSTOM_TAXONOMY_DEFAULT_ID
      : (taxonomyId.split('custom_role_')[1] ?? CUSTOM_TAXONOMY_DEFAULT_ID); // matches tables names like rics_k50_10, rics_k10_10

  return {
    id: customTaxonomyId,
    parentId: undefined,
    selectionListId:
      HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY as unknown as SelectionListIdNames,
    item: {
      id: customTaxonomyId,
      label: customTaxonomyId,
      shortName: customTaxonomyId,
    },
    children: [],
  };
};

const createCustomTaxonomyVersionTreeItem = (
  version: string
): TreeItem<string> => {
  return {
    id: version,
    parentId: undefined,
    selectionListId:
      HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY_VERSION as unknown as SelectionListIdNames,
    item: {
      id: version,
      label: version,
      shortName: version,
    },
    children: [],
  };
};

const hasSelectionListFilter = (
  column: NonNullable<ReportEntityConfig['columns']>[number],
  selectionListId: string
) =>
  column.reportEntityOptions.selectionList.filters.some(
    (filter) => String(filter) === selectionListId
  );

/**
 * Custom hook to get the custom taxonomy filter value from the report builder store
 * @returns CustomRoleTaxonomySelection from getRoleTaxonomyFilterValue
 */
export const useCustomTaxonomyFilterValue = (): CustomRoleTaxonomySelection => {
  const reportBuilderDeliverables = useReportBuilderSelector(selectAllReports);

  const report = Object.values(reportBuilderDeliverables)?.[0];

  const customTaxonomySelection = report?.selections
    ? Object.values(report.selections)
        .flatMap((selectionGroup) => Object.values(selectionGroup))
        .find(
          (selectionItem) =>
            String(selectionItem.selectionListId) ===
            HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY
        )
    : undefined;

  // Get the taxonomy ID from the stored selection or default to 'default'
  const taxonomyId = customTaxonomySelection
    ? customTaxonomySelection.id || CUSTOM_TAXONOMY_DEFAULT_ID
    : CUSTOM_TAXONOMY_DEFAULT_ID;

  return getRoleTaxonomyFilterValue(taxonomyId);
};

export const useReportsRoleCustomTaxonomy = (
  entityConfigs: ReportEntityConfig | null,
  selectedReportId?: number,
  entitySelections?: EntitySelections
) => {
  const { getSpecificTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  // Load rics_k400 selection list for adaptive taxonomy determination
  const selectionListState = useSelectionLists(['rics_k400']);
  const ricsK400List = selectionListState?.rics_k400 as
    | RicsSelectionList<'rics_k400'>
    | undefined;

  /*
   * Updates the following mainEntities for the report configuration:
   * - custom_taxonomy
   * - custom_taxonomy_version
   */
  const updateReportsRoleTaxonomy = useCallback(
    (roleTaxonomy: CustomRoleTaxonomyId) => {
      if (
        !entityConfigs ||
        selectedReportId === undefined ||
        selectedReportId === null
      ) {
        return;
      }
      const adaptiveSelections =
        createCustomTaxonomyTreeItemFromId(roleTaxonomy);

      const customTaxonomyColumn = entityConfigs.columns?.find((c) =>
        hasSelectionListFilter(c, HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY)
      );
      if (customTaxonomyColumn) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: customTaxonomyColumn.id,
          selections: {
            [CUSTOM_TAXONOMY_DEFAULT_ID]: adaptiveSelections,
          },
        });
      }

      const customTaxonomyVersionColumn = entityConfigs.columns?.find((c) =>
        hasSelectionListFilter(c, HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY_VERSION)
      );
      const taxonomySelection = selectionFromCustomRoleTaxonomyId(roleTaxonomy);
      const version = getSpecificTaxonomyVersion(taxonomySelection);
      if (customTaxonomyVersionColumn && version) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: customTaxonomyVersionColumn.id,
          selections: {
            [HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY_VERSION]:
              createCustomTaxonomyVersionTreeItem(version),
          },
        });
      }
    },
    [entityConfigs, selectedReportId, getSpecificTaxonomyVersion]
  );

  /*
   * Inject default selection for custom_taxonomy main entity into the report builder store
   * TODO: in future support adapting based on the other selections.
   */
  useLayoutEffect(() => {
    if (
      !entityConfigs ||
      selectedReportId === undefined ||
      selectedReportId === null
    ) {
      return;
    }

    const hasHiddenRoleTaxonomyField =
      entityConfigs.columns?.some(
        (column) =>
          hasSelectionListFilter(
            column,
            HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY
          ) ||
          hasSelectionListFilter(
            column,
            HIDDEN_REPORT_FIELDS.CUSTOM_TAXONOMY_VERSION
          )
      ) ?? false;

    if (!hasHiddenRoleTaxonomyField) return;

    // Wait for the K400 list to load before computing the taxonomy.
    // The effect will re-run once ricsK400List becomes available.
    if (!ricsK400List) return;

    const ricsSelections = adaptiveRicsFromReportSelections(entitySelections);
    const adaptiveRoleTaxonomy = getAdaptiveRoleTaxonomyValue({
      ricsK400List,
      ricsSelections,
    });

    updateReportsRoleTaxonomy(adaptiveRoleTaxonomy);
  }, [
    entityConfigs,
    selectedReportId,
    entitySelections,
    ricsK400List,
    updateReportsRoleTaxonomy,
  ]);
};
